import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUnionContacts, searchUnionContacts } from 'model/contact';
import { getAreas } from 'model/room';
import { RootState } from 'model';
import { AreaV2 } from 'type/room';
import { ContactMemberV2, ContactV2 } from 'type/contact';
import { setCheckedAreas, getCheckedAreas, clearCheckedAreas, getStaffId } from 'data/storage';
import { IMeetingOrderFn, IMeetingOrderForm, OrderSettings } from 'type/meeting';
import { RoomScheduleFilterForm, RoomScheduleService } from 'nicehubs-plus';
import { Room, Meeting } from 'api';
import { useListenScheduleChange } from './useListenSchedule';

export interface RoomScheduleRef {
  /** 日程表默认筛选参数 */
  filterValue?: RoomScheduleFilterForm;
  /** 需要闪烁的日程 */
  blinkList: string[];
  /** 预约设置 */
  orderSettings: OrderSettings;
  /** 区域数据 */
  areaData: AreaV2[];
  /** 成员数据 */
  memberData: ContactV2;
  /** 筛选参会人 */
  filterMember: (keyword?: string) => void;
  /** 禁用成员 */
  disabledMember: (node: ContactV2 | ContactMemberV2) => boolean;
  /** 点击预约 */
  confirm: IMeetingOrderFn;
  /** 点击取消 */
  cancel: () => void;
  /** 点击更多 */
  clickMore: (form: IMeetingOrderForm) => void;
  /** 日程服务 */
  service: RoomScheduleService;

  addEventListener: any;
  removeEventListener: any;
}

export const useScheduleTable = <T>() => {
  const dispatch = useDispatch();
  const componentRef = useRef<RoomScheduleRef & T>();

  const orderSettings = useSelector((state: RootState) => state.meeting.orderSettings);
  const areaData = useSelector((state: RootState) => state.room.area);
  const unionContacts = useSelector((state: RootState) => state.contact.unionContacts);
  const [displayContacts, setDisplayContacts] = useState<ContactV2>();
  const [blinkList, setBlinkList] = useState<string[]>([]);
  const [filterValue, setFilterValue] = useState<RoomScheduleFilterForm>(() => {
    const checkAreas = getCheckedAreas();
    return { areaIds: checkAreas ? checkAreas.split(',') : undefined };
  });
  const { addListener, removeListener, emitScheduleChange } = useListenScheduleChange();

  /** 搜索参会人 */
  const filterMember = useCallback(
    (keyword?: string) => {
      if (!keyword) {
        componentRef.current.nodeOrder = 'teamFirst';
        if (unionContacts) {
          setDisplayContacts(unionContacts);
        } else {
          dispatch(
            getUnionContacts({
              callback: (data) => {
                setDisplayContacts(data);
              }
            })
          );
        }
      } else {
        componentRef.current.nodeOrder = 'menberFirst';
        dispatch(
          searchUnionContacts({
            keyword,
            callback: (data) => {
              setDisplayContacts(data);
            }
          })
        );
      }
    },
    [unionContacts]
  );

  /** 禁止选择自己 */
  const disabledMember = (node: ContactV2 | ContactMemberV2) => {
    return node.refId === getStaffId();
  };

  const init = async () => {
    dispatch(getAreas());
    dispatch(
      getUnionContacts({
        callback: (data) => {
          setDisplayContacts(data);
        }
      })
    );
  };

  /** 设置会议室筛选参数 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.filterValue = filterValue;
  }, [filterValue]);

  /** 设置日程条闪烁属性 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.blinkList = blinkList;
  }, [blinkList]);

  /** 设置会议室、会议室日程表格属性 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.areaData = areaData;
  }, [areaData]);

  /** 设置成员选择属性 */
  useEffect(() => {
    if (!componentRef.current || !displayContacts) return;
    componentRef.current.memberData = displayContacts;
  }, [displayContacts]);

  /** 设置企业会议预约配置属性 */
  useEffect(() => {
    if (!componentRef.current || !orderSettings) return;
    componentRef.current.orderSettings = orderSettings;
  }, [orderSettings]);

  /** 筛选参会人 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.filterMember = filterMember;
  }, [filterMember]);

  /** 获取会议室 */
  const getRoomsV2: RoomScheduleService['getRoomsV2'] = async (params) => {
    try {
      // 当前选中
      const currentCheckAreas = params.areaIdList?.join(',') || '';
      // 筛选记忆
      currentCheckAreas ? setCheckedAreas(currentCheckAreas) : clearCheckedAreas();
    } catch (err) {
      console.error('记录空间筛选结果失败', err);
    }
    console.log('🌶🌶🌶获取会议室');

    const res = await Room.getBookRoomV2(params).toPromise();
    return res.data;
  };

  /** 获取会议室日程 */
  const getRoomSchedulesV2: RoomScheduleService['getRoomSchedulesV2'] = async (params) => {
    const res = await Room.getRoomSchedulesV2(params).toPromise();
    return res.data;
  };

  /* 获取人员日程  */
  const getMemberSchedules: RoomScheduleService['getMemberSchedules'] = async (params) => {
    const roomRes = await Meeting.getMemberSchedules(params).toPromise();
    return roomRes.data;
  };

  /** 会议室选择弹窗显示/隐藏 */
  const onRoomDialogVisibleChange = ({ detail }: { detail: [boolean] }) => {
    // 会议室表单打开弹窗时，使用最新的区域数据
    const visible = detail[0];
    if (visible && componentRef.current) {
      const checkAreas = getCheckedAreas();
      setFilterValue({ areaIds: checkAreas ? checkAreas.split(',') : undefined });
    }
  };

  /** 属性设置、事件监听 */
  useEffect(() => {
    init();
    if (!componentRef.current) return;
    componentRef.current.disabledMember = disabledMember;

    /** 日程相关服务 */
    componentRef.current.service = {
      getRoomsV2,
      getRoomSchedulesV2,
      getMemberSchedules,
      addScheduleListener: addListener
      /** 触发时机不对，使用 useEffect return 移除监听 */
      // removeScheduleListener: removeListener
    };

    // 监听变化
    componentRef.current?.addEventListener('room-dialog-visible-change', onRoomDialogVisibleChange);

    return () => {
      removeListener();
      componentRef.current?.removeEventListener('room-dialog-visible-change', onRoomDialogVisibleChange);
    };
  }, []);

  return {
    componentRef,
    setBlinkList,
    emitScheduleChange
  };
};

export default useScheduleTable;
